<template>
  <div class="kanban-container">
    <div class="kanban-board">
      <div
        v-for="column in columns"
        :key="column.status"
        class="kanban-column"
      >
        <div class="column-header" :style="{ borderTopColor: column.color }">
          <span class="column-title">{{ column.label }}</span>
          <span class="column-count">{{ column.items.length }}</span>
        </div>
        <div class="column-content">
          <div
            v-for="item in column.items"
            :key="item.pedidoId"
            class="kanban-card"
            @click="$emit('view-details', item)"
          >
            <div class="card-header">
              <span class="card-order-id">{{ item.pedidoNumeroCliente || '-' }}</span>
              <span class="card-erp" v-if="item.pedidoNumeroIntegracao">{{ item.pedidoNumeroIntegracao }}</span>
            </div>
            <div class="card-client">
              <feather-icon icon="UserIcon" size="14" />
              <span>{{ formatCliente(item) }}</span>
            </div>
            <div class="card-details">
              <div class="detail-item">
                <feather-icon icon="CalendarIcon" size="12" />
                <span>{{ item.pedidoData || '-' }}</span>
              </div>
              <div class="detail-item">
                <feather-icon icon="TruckIcon" size="12" />
                <span>{{ item.pedidoDataEntregaPrevista || '-' }}</span>
              </div>
            </div>
            <div class="card-footer">
              <span class="card-value">{{ formatValorComImpostos(item) }}</span>
              <button class="card-action" @click.stop="$emit('view-details', item)">
                <feather-icon icon="EyeIcon" size="14" />
              </button>
            </div>
          </div>
          <div v-if="column.items.length === 0" class="empty-column">
            <feather-icon icon="InboxIcon" size="24" />
            <span>Sem pedidos</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KanbanView',
  props: {
    orders: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    columns() {
      const statusGroups = {};

      this.orders.forEach((order) => {
        const status = String(order.pedidoSitSales || order.pedidoSitComercial || 'SEM STATUS').toUpperCase().trim();
        if (!statusGroups[status]) {
          statusGroups[status] = [];
        }
        statusGroups[status].push(order);
      });

      const statusOrder = ['DIGITADO', 'INTEGRADO', 'APROVADO', 'FATURADO', 'CANCELADO', 'REJEITADO', 'RECALCULAR'];
      const orderedStatuses = [...statusOrder.filter((s) => statusGroups[s]), ...Object.keys(statusGroups).filter((s) => !statusOrder.includes(s))];

      return orderedStatuses.map((status) => ({
        status,
        label: this.getLabel(status),
        color: this.getColor(status),
        items: statusGroups[status] || [],
      }));
    },
  },
  methods: {
    formatCliente(item) {
      const id = item.clienteIdERP ? String(item.clienteIdERP) : '';
      const nome = item.clienteRazaoSocial || 'Cliente não informado';
      return id ? `${id} - ${nome}` : nome;
    },
    formatValorComImpostos(item) {
      if (item.pedidototalcomimpostos !== null && item.pedidototalcomimpostos !== undefined && item.pedidototalcomimpostos !== '') {
        const valor = Number(item.pedidototalcomimpostos);
        return Number.isNaN(valor) ? item.pedidototalcomimpostos : this.formatCurrency(valor);
      }
      return item.pedidoValorTotal || 'R$ 0,00';
    },
    getLabel(status) {
      const labels = {
        DIGITADO: 'Digitados',
        INTEGRADO: 'Integrados',
        APROVADO: 'Aprovados',
        CANCELADO: 'Cancelados',
        REJEITADO: 'Rejeitados',
        RECALCULAR: 'Recalcular',
        FATURADO: 'Faturados',
        PENDENTE: 'Pendentes',
        'SEM STATUS': 'Sem Status',
      };
      return labels[status] || status;
    },
    getColor(status) {
      const colors = {
        DIGITADO: '#6366f1',
        INTEGRADO: '#10b981',
        APROVADO: '#22c55e',
        CANCELADO: '#ef4444',
        REJEITADO: '#f97316',
        RECALCULAR: '#8b5cf6',
        FATURADO: '#14b8a6',
        PENDENTE: '#f59e0b',
      };
      return colors[status] || '#64748b';
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return 'R$ 0,00';
      const num = Number(value);
      if (isNaN(num)) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(num);
    },
  },
};
</script>

<style scoped>
.kanban-container {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-board {
  display: flex;
  gap: 1rem;
  min-width: max-content;
  padding: 0.5rem;
}

.kanban-column {
  width: 300px;
  min-width: 300px;
  background: var(--surface-bg, #f8fafc);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 320px);
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border-top: 3px solid;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background: var(--card-bg, #ffffff);
}

.column-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.column-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--surface-bg, #f8fafc);
  color: var(--text-secondary, #64748b);
  border-radius: 100px;
}

.column-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.kanban-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  padding: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.kanban-card:hover {
  border-color: var(--primary, #7c3aed);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.625rem;
}

.card-order-id {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary, #1e293b);
}

.card-erp {
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
  font-family: monospace;
}

.card-client {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
  margin-bottom: 0.625rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-client svg {
  flex-shrink: 0;
  color: var(--text-muted, #94a3b8);
}

.card-details {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.625rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.625rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.card-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.card-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.card-action:hover {
  background: rgba(59, 130, 246, 0.2);
}

.empty-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--text-muted, #94a3b8);
  font-size: 0.8125rem;
}

body.dark-layout .kanban-column {
  background: var(--dark-surface-bg, #242b3d);
}

body.dark-layout .column-header {
  background: var(--dark-card-bg, #283046);
}

body.dark-layout .column-title {
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .column-count {
  background: var(--dark-surface-bg, #242b3d);
  color: var(--dark-text-secondary, #b4b7bd);
}

body.dark-layout .kanban-card {
  background: var(--dark-card-bg, #283046);
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .card-order-id,
body.dark-layout .card-value {
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .card-client {
  color: var(--dark-text-secondary, #b4b7bd);
}

body.dark-layout .card-footer {
  border-color: var(--dark-border, #3b4253);
}
</style>
